import * as monaco from 'monaco-editor';
import {
  getuuid,
  tips,
  getFilteredHeadObjectData,
  getFileEndType,
} from '@/scripts/tools/utils';
import dialog from '@/scripts/tools/dialog';
import { config } from '@/scripts/tools/config';
import charsetUtil from '@/scripts/helper/editor/charsetUtil';
import CHARSET from '@/scripts/helper/editor/charset';
import editorTools from '@/scripts/helper/editor/tools';
import cos from '@/scripts/helper/cos';
import { getObjectMetaAndAcl } from '@/scripts/helper/cos/cosSdkUtils';

monaco.editor.defineTheme('BlackTheme', {
  base: 'vs-dark',
  inherit: true,
  rules: [{ background: '#1c1c1c' }],
  colors: {
    // 相关颜色属性配置
    // 'editor.foreground': '#000000',
    'editor.background': '#1c1c1c', // 背景色
    // 'editorCursor.foreground': '#8B0000',
    // 'editor.lineHighlightBackground': '#0000FF20',
    // 'editorLineNumber.foreground': '#008800',
    // 'editor.selectionBackground': '#88000030',
    // 'editor.inactiveSelectionBackground': '#88000015'
  },
});

export default {
  props: {
    fileInfo: {
      type: Object,
      required: false,
    },
    bucketInfo: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      exitSaveShow: false,
      renameShow: false,

      fileBuffer: null,
      fileContent: '',
      readPercent: 0,
      writePercent: 0,
      // 界面状态
      reading: false,
      writing: false,
      errorMsg: '',
      // 编码
      fileCharset: 'utf8',
      charset: 'utf8',
      CHARSET,
      saveCharset: ['utf8', 'utf8bom', 'gbk', 'gb2312'],
      // 换行符
      endOfLine: 'LF',
      manocoEditor: null,
      languageList: editorTools.languageList,
      currLanguage: 'javascript',
      notAllowedEdit: false,
      editorRect: {
        width: 900,
        height: 900,
      },
    };
  },
  computed: {
    /**
     * 代理: 更新当前文件是否处于未保存状态
     */
    unsavedProxy: {
      get() {
        return this.fileInfo.unsaved;
      },
      set(val) {
        this.$emit('file-change', { ...this.fileInfo, unsaved: val });
      },
    },
  },
  watch: {
    errorMsg(val) {
      if (val) {
        this.$emit('file-change', { ...this.fileInfo, error: true });
      }
    },
  },
  methods: {
    initData() {
      this.errorMsg = '';
      this.readPercent = 0;
      this.endOfLine = 'LF';
      this.charset = 'utf8';
      this.reading = true;
      this.readTaskId = getuuid();
      this.notAllowedEdit = false;
      this.fileContent = '';
      this.fileBuffer = '';

      this.initRectRender();
    },
    initRectRender() {
      const computedWidth = Math.floor((document.body.clientWidth * 4) / 5);
      const computedHeight = Math.floor((document.body.clientHeight * 4) / 5);
      this.editorRect = {
        width: Math.max(computedWidth, 900),
        height: Math.max(computedHeight, 400),
      };
    },
    async resetEditor() {
      const { container } = this.$refs;
      container.innerHTML = '';
      this.currLanguage = await editorTools.getMimeType(this.fileInfo.key);

      this.manocoEditor = monaco.editor.create(container, {
        theme: 'BlackTheme',
        language: this.currLanguage,
        value: this.fileContent,
        roundedSelection: false,
        autoIndent: true,
        minimap: {
          enabled: false,
        },
        automaticLayout: true,
      });
      this.manocoEditor.onDidChangeModelContent(() => {
        this.fileBuffer = null;
        this.resetUnsaved();
      });
      this.manocoEditor.onKeyDown(e => {
        if ((e.ctrlKey || e.metaKey) && e.keyCode === 49) {
          this.saveFile();
          e.preventDefault();
        }
      });
    },
    onStillEdit() {
      this.editFile('plaintext');
    },
    editFile(forceEditAsType) {
      if (this.fileInfo.size > config.editTextMaxSize) {
        const msg = this.$t('error.edit_file_too_large', { num: 10 });
        this.errorMsg = msg;
        this.innerShow = false;
        this.notAllowedEdit = true;
        return;
      }

      this.initData();

      const taskId = this.readTaskId;
      cos.setSyncUploading(this.readTaskId);

      const opt = {
        onProgress: info => {
          this.readPercent = info.percent;
        },
        TaskId: this.readTaskId,
        Key: this.fileInfo.key,
        ...this.bucketInfo,
        DataType: 'arraybuffer',
        ResponseCacheControl: 'no-cache',
      };

      const allowEdit = forceEditAsType || editorTools.getMimeType(this.fileInfo.key);
      if (!allowEdit) {
        this.reading = false;
        this.notAllowedEdit = true;
        this.errorMsg = window.lang.err('error.not_allowed_edit');
        return;
      }
      cos.getObject(opt, (err, data) => {
        cos.removeSyncUploading(taskId);
        if (!this.innerShow) return;
        this.reading = false;
        if (err) {
          let { code } = err;
          let message = '';
          if (!code && err.body) {
            const errBuffer = err.body;
            const errorXml = charsetUtil.toStr(errBuffer, 'utf8');
            [, code] = errorXml.match(/\<Code\>(.*)\<\/Code\>/);
            [, message] = errorXml.match(/\<Message\>(.*)\<\/Message\>/);
          }
          this.errorMsg = window.lang.err(`error.${code}`, message || err);
          return;
        }

        this.$nextTick(() => {
          this.fileBuffer = data.Body;
          this.fileCharset = charsetUtil.detectEncodingFromBuffer(data.Body) || 'utf8';
          this.charset = this.fileCharset;
          this.endOfLine = this.fileContent.includes('\r\n') ? 'CRLF' : 'LF';

          this.resetContent();
          this.resetEditor();
          this.fetchHeadObjectData();
          // 每次打开编辑弹窗则保存文件编辑前的历史状态
          editorTools.saveFileToLocal(opt.Key, data.Body);
        });
      });
    },
    async fetchHeadObjectData() {
      try {
        this.HeadObjectData = await getFilteredHeadObjectData(
          this.bucketInfo,
          this.fileInfo,
        );
      } catch (error) {
        // 处理错误
        console.log(error, '头部过滤失败');
      }
    },
    resetUnsaved() {
      if (!this.manocoEditor) return;
      this.unsavedProxy = this.manocoEditor.getValue() !== this.fileContent || this.charset !== this.fileCharset;
    },
    resetContent() {
      this.fileContent = charsetUtil.toStr(this.fileBuffer, this.charset);
    },
    selectEndOfLine(type) {
      if (!this.manocoEditor) return;
      let content = this.manocoEditor.getValue();
      if (type === 'CRLF') {
        content = content.replace(/\r\n|\r|\n/g, '\r\n');
      } else if (type === 'LF') {
        content = content.replace(/\r\n|\r|\n/g, '\n');
      } else if (type === 'CR') {
        content = content.replace(/\r\n|\n|\r/g, '\r');
      }
      this.endOfLine = type;
      this.manocoEditor.setValue(content);
      this.resetUnsaved();
    },
    selectLanguage(language) {
      this.currLanguage = language;
      const model = this.manocoEditor.getModel();
      monaco.editor.setModelLanguage(model, language);
    },
    openWithCharset(charset) {
      if (this.fileBuffer) {
        this.fileCharset = charset;
        this.charset = charset;
        this.resetContent();
        this.resetEditor();
      } else {
        tips.error(this.$t('error.modified_file_need_save'));
      }
    },
    saveWithCharset(charset) {
      this.charset = charset;
      this.resetUnsaved();
    },
    async checkIfCanSave({ curCharset, curContent, saveBuf }) {
      const dialogAsync = message => new Promise(resolve => {
        setTimeout(() => {
          dialog.confirm({
            title: this.$t('core.default_title'),
            message,
          }, confirm => {
            resolve(confirm);
          });
        }, 500);
      });
      let canSave = true;
      // 检查转换编码后，还能否转回来
      if (charsetUtil.toStr(saveBuf, curCharset) !== curContent) {
        const message = this.$t('error.not_support_save_as_encode_param', {
          encode: this.CHARSET[curCharset].labelShort,
        }) + this.$t('core.confirm_to_save');
        canSave = await dialogAsync(message);
      }

      if (canSave && editorTools.hasGarbled(curContent)) {
        const message = this.$t('error.content_exist_garbled', {
          encode: this.CHARSET[curCharset].labelShort,
        }) + this.$t('core.confirm_to_save');
        canSave = await dialogAsync(message);
      }

      return canSave;
    },
    async saveFile(isExit, rename) {
      if (!this.innerShow || !this.unsavedProxy) {
        if (!rename) return;
      } else if (!this.fileInfo) {
        tips.error(this.$t('error.NoSuchKey'));
        return;
      }

      tips.loading(this.$t('core.edit_saving'));
      const curCharset = this.charset;
      const curContent = this.manocoEditor.getValue();
      const saveBuf = charsetUtil.toBuf(curContent, curCharset);
      const saveBlob = new Blob([saveBuf]);
      // 把内容转为保存的编码
      if (saveBuf === false) {
        tips.closeTips(); // 关闭加载提示
        console.log(`转换为 ${this.CHARSET[curCharset].labelShort} 编码失败`);
        return;
      }

      this.writing = true;
      const checkRes = await this.checkIfCanSave({
        curCharset,
        curContent,
        saveBuf,
      });
      if (!checkRes) {
        this.writing = false;
        tips.closeTips(); // 修复：关闭加载提示
        return;
      }

      // 兼容另存为后缀命名及content-type统一
      let headers = {};
      if (rename) {
        const fileType = getFileEndType(rename);
        headers = { ...this.HeadObjectData, 'content-type': fileType || 'application/octet-stream' };
      } else {
        headers = this.HeadObjectData;
      }
      // 保存到 COS
      const opt = {
        Body: saveBlob,
        SkipTask: true,
        Key: rename || this.fileInfo.key,
        ...this.bucketInfo,
        Headers: headers,
      };
      getObjectMetaAndAcl(opt, (err1, data) => {
        if (data) {
          const { ACL = 'default', StorageClass } = data;
          opt.ACL = ACL;
          opt.StorageClass = StorageClass;
        } else {
          opt.ACL = 'default';
        }

        cos.putObject(opt, err => {
          tips.closeTips();
          this.writing = false;
          if (err) {
            tips.error(this.$t('state.fail.save'));
          } else {
            this.fileCharset = curCharset;
            this.fileContent = curContent;
            this.fileBuffer = charsetUtil.arr2buf(saveBuf);
            !rename && this.resetUnsaved();
            tips.success(this.$t('state.success.save'));
            if (isExit === true) this.innerShow = false;
          }
        });
      });
    },
    saveFileAs() {
      this.renameShow = true;
    },
  },
};
